/**
 * Wizard Data → API Request DTO mappers
 *
 * Transforms frontend wizard state into the exact shapes the V2 backend expects.
 * Each mapper is pure (no side effects) and stateless.
 */
import moment from 'moment';
import type {
  CreateOpportunityRequest,
  CreateOpportunityGroupRequest,
  CreateDealAssetRequest,
  ApiContentDto,
  ApiPaymentDto,
  InitInfoMerchant,
  RatsRate,
  RatsRateTier,
} from './types';
import { DealAssetStatus, IoAttachmentType, RateType } from './types';
import type { BaselineTier } from '../../pages/CreateUpsize/mockData';
import type { WizardData, UpsizeOpp } from '../../pages/CreateUpsize/mockData';
import type { AdSalesWizardData } from '../../pages/CreateAdSales/mockData';
import { isFullWindowAsset, mergeConsecutiveDays } from '../../pages/CreateAdSales/mockData';
import type { OfferFormData } from '../../pages/CreateStandaloneOffer/offerForm';

// ─── Helpers ────────────────────────────────────────────────

/** Combine a Date + HH:mm string → ISO 8601 datetime. Throws if date is null (caller should validate). */
function toIsoDatetime(date: Date | null, time?: string): string {
  if (!date) throw new Error('toIsoDatetime: date is required — check wizard validation');
  const m = moment(date);
  if (time) {
    const [h, min] = time.split(':').map(Number);
    m.hour(h || 0)
      .minute(min || 0)
      .second(0)
      .millisecond(0);
  } else {
    m.startOf('day');
  }
  return m.toISOString();
}

// ─── UPSIZE ─────────────────────────────────────────────────

/** Map enabled ratsRate tiers from the V2 baseline API to the UI's BaselineTier shape. */
export function mapRatsRateToBaselineTiers(tiers: RatsRateTier[]): BaselineTier[] {
  return tiers
    .filter((t) => t.enabled)
    .map((t) => ({
      id: t.tierLabel,
      category: t.marketingLabel || t.tierLabel,
      baseline: t.commissionRate,
      unit: t.commissionType === RateType.PERCENTAGE ? ('%' as const) : ('$' as const),
    }));
}

/** Derive the give (cashback) amount for a tier at submit time.
 *  Option B from the FE integration doc — margin-preserving default give. */
function deriveGiveAmount(get: number, baselineTier: RatsRateTier, ratsRate: RatsRate): string {
  if (ratsRate.isSetFixedMargin) {
    return String(Math.round(get * (1 - ratsRate.targetMarginRate / 100) * 100) / 100);
  }
  return String(baselineTier.cashbackRate);
}

/**
 * Map a single UpsizeOpp → flat RatsRateTier array for the V2 ratsRate write shape.
 * Flattens the legacy nested `rate` wrapper; numeric rates (not strings).
 */
function mapUpsizeTiers(opp: UpsizeOpp): RatsRateTier[] {
  return opp.tiers
    .filter((t) => t.rate > 0)
    .map((t) => {
      const commissionType = t.unit === '$' ? RateType.FIXED : RateType.PERCENTAGE;
      const baselineTier = opp._baseline?.tiers.find((b) => b.tierLabel === t.id);

      const cashbackRate =
        baselineTier && opp._baseline ? Number(deriveGiveAmount(Number(t.rate), baselineTier, opp._baseline)) : 0;

      return {
        tierLabel: t.id,
        marketingLabel: t.category,
        isNewTier: false,
        enabled: true,
        commissionType,
        commissionRate: Number(t.rate),
        commissionCap: baselineTier?.commissionCap ?? null,
        cashbackType: baselineTier ? baselineTier.cashbackType : commissionType,
        cashbackRate,
        cashbackCap: baselineTier?.cashbackCap ?? null,
      };
    });
}

/**
 * Map the entire Upsize wizard → array of CreateOpportunityRequest (one per opportunity).
 * Each UpsizeOpp becomes a separate POST /opportunities call.
 */
export function mapUpsizeWizardToRequests(
  data: WizardData,
  merchant: InitInfoMerchant,
  operatorEmail: string
): CreateOpportunityRequest[] {
  return data.opportunities.map((opp) => {
    const b = opp._baseline;
    return {
      opportunityType: 'UPSIZE' as const,
      status: 'PENDING_BD' as const,
      operator: operatorEmail,
      merchantId: merchant.merchantId,
      campaignId: opp.campaignId || undefined,
      country: merchant.country,
      startDate: toIsoDatetime(opp.startDate, opp.startTime),
      endDate: toIsoDatetime(opp.endDate, opp.endTime),
      packageTier: opp.package || undefined,
      merchantComment: data.merchantComment || undefined,
      ratsRate: {
        isSetOrderCap: b?.isSetOrderCap ?? false,
        orderCap: b?.isSetOrderCap ? b.orderCap ?? null : null,
        orderCommissionCap: b?.isSetOrderCap ? b.orderCommissionCap ?? null : null,
        isSetFixedMargin: b?.isSetFixedMargin ?? false,
        targetMarginRate: b?.targetMarginRate ?? 0,
        tiers: mapUpsizeTiers(opp),
      },
      ...(opp.networkProof?.length && {
        upsize: { networkCheckScreenshots: opp.networkProof.map((f) => f.uid) },
      }),
    };
  });
}

// ─── AD_SALES ───────────────────────────────────────────────

/**
 * Map the Ad Sales wizard → CreateOpportunityGroupRequest.
 * selected[] instances become packages[]; invoicing data goes into group.payment.
 */
export function mapAdSalesWizardToRequest(
  data: AdSalesWizardData,
  merchant: InitInfoMerchant,
  gstRate = 0.09
): CreateOpportunityGroupRequest {
  const currency = data.bankAccount?.billingCurrency || 'SGD';
  return {
    type: 'AD_SALES',
    country: merchant.country,
    merchantId: merchant.merchantId,
    group: {
      status: 'PENDING_BD' as const,
      payment: (() => {
        const totalPrice = data.selected.reduce((sum, inst) => sum + (inst.packagePrice || 0), 0);
        // gstRate passed as parameter, defaults to 0.09 (SG)
        const totalGstVat = Math.round(totalPrice * gstRate * 100) / 100;
        const grandTotal = totalPrice + totalGstVat;
        const invoiceDate = data.selected[0]?.startDate
          ? moment(data.selected[0].startDate).format('YYYY-MM-DD')
          : moment().format('YYYY-MM-DD');

        return {
          billingInfo: data.billingType === 'Network' ? 'Agency' : 'Direct',

          invoicingUnit: {
            // ── Required IO-generation fields ──────────────────────────
            companyName: data.legalEntity?.accountName || '',
            // joined single-line address consumed by IO PDF generation
            address: [
              data.legalEntity?.invoicingAddress?.street,
              data.legalEntity?.invoicingAddress?.unit,
              data.legalEntity?.invoicingAddress?.postalCode,
            ]
              .filter(Boolean)
              .join(', '),
            email: data.bankAccount?.contactEmail || '',
            // ── Extended merchant-facing fields (MIC-2898) ─────────────
            // Stored in payment.details JSONB (wholesale), surfaced on GET.
            ...(data.legalEntity?.country && { country: data.legalEntity.country }),
            ...(data.legalEntity?.companyRegNumber && {
              companyRegistrationNumber: data.legalEntity.companyRegNumber,
            }),
            ...(data.legalEntity?.taxNumber && {
              taxRegistrationNumber: data.legalEntity.taxNumber,
            }),
            ...(data.legalEntity?.invoicingAddress && {
              invoicingAddress: {
                street: data.legalEntity.invoicingAddress.street,
                ...(data.legalEntity.invoicingAddress.unit && { unit: data.legalEntity.invoicingAddress.unit }),
                ...(data.legalEntity.invoicingAddress.postalCode && {
                  postalCode: data.legalEntity.invoicingAddress.postalCode,
                }),
              },
            }),
            ...(data.bankAccount?.name && { accountName: data.bankAccount.name }),
            ...(data.bankAccount?.holderName && { accountHolderName: data.bankAccount.holderName }),
            ...(data.bankAccount?.billingCurrency && { billingCurrency: data.bankAccount.billingCurrency }),
            ...(data.bankAccount?.billingAddress && {
              billingAddress: {
                street: data.bankAccount.billingAddress.street,
                ...(data.bankAccount.billingAddress.unit && { unit: data.bankAccount.billingAddress.unit }),
                ...(data.bankAccount.billingAddress.postalCode && {
                  postalCode: data.bankAccount.billingAddress.postalCode,
                }),
              },
            }),
            ...(data.bankAccount?.mailingAddress && {
              mailingAddress: {
                street: data.bankAccount.mailingAddress.street,
                ...(data.bankAccount.mailingAddress.unit && { unit: data.bankAccount.mailingAddress.unit }),
                ...(data.bankAccount.mailingAddress.postalCode && {
                  postalCode: data.bankAccount.mailingAddress.postalCode,
                }),
              },
            }),
            billingSameAsInvoicing: data.bankAccount?.billingSameAsInvoicing ?? true,
            mailingSameAsBilling: data.bankAccount?.mailingSameAsBilling ?? true,
          },

          summary: {
            currency,
            totalPrice: totalPrice.toFixed(2),
            totalGstVat: totalGstVat.toFixed(2),
            grandTotal: grandTotal.toFixed(2),
          },

          scheduleDetail: {
            currency,
            totalSalesPrice: totalPrice.toFixed(2),
            paymentMode: 'FULL_PAYMENT' as const,
            gstVatRateType: 'PERCENTAGE',
            gstVatRate: (gstRate * 100).toFixed(1),
            authorizerName: data.bankAccount?.holderName || '',
            authorizerEmail: data.bankAccount?.contactEmail || '',
          },

          invoiceSchedules: [
            {
              paymentRefId: data.poNumber || `IO-${Date.now()}`,
              invoiceDate,
              price: totalPrice.toFixed(2),
              gstVat: totalGstVat.toFixed(2),
              total: grandTotal.toFixed(2),
            },
          ],

          insertionOrderReferenceId: data.poNumber || undefined,

          ioAttachments: data.signedIO
            ?.filter((f) => f.fileKey)
            .map((f) => ({ fileKey: f.fileKey as string, name: f.name, type: IoAttachmentType.SIGNED })),
        } as ApiPaymentDto;
      })(),
    },
    packages: data.selected.map((inst) => {
      // Build assets[] for each package instance.
      // inst.assets carries the flattened asset list (set when the instance was created).
      // inst.assetSchedule carries the picked days keyed by asset name.
      const instAssets = inst.assets ?? [];

      // Build a minimal package-like object to test isFullWindowAsset — the helper only
      // needs a surfaces[] with the assets list to compute the max qty.
      const fakePkg = {
        surfaces: [{ label: '', assets: instAssets }],
      } as import('../../pages/CreateAdSales/mockData').AdSalesPackage;

      const assets = instAssets.map((a) => {
        const pickedDays: Date[] = (inst.assetSchedule?.[a.name] ?? []).filter(Boolean) as Date[];
        // Full-window: asset covers the entire booking window, so no per-day picks are stored.
        // Catalog-driven: use isFullWindowAsset (qty vs package duration).
        // API-driven: pickedDays is the source of truth — if picks exist, they must be sent
        // regardless of isFullWindowAsset (which breaks for API assets because a.qty is undefined).
        const isFullWindow = pickedDays.length === 0 && isFullWindowAsset(a, fakePkg);

        const mergedRanges = !isFullWindow && pickedDays.length > 0 ? mergeConsecutiveDays(pickedDays) : [];
        // Anchor each slot boundary to UTC midnight of the picked calendar day. Decoders
        // (FE mapper.ts reload + BE soft-book day-enumeration) floor via setUTCHours(0,0,0,0);
        // emitting local-midnight here (moment().startOf('day')) shifts every slot one UTC day
        // earlier in east-of-UTC timezones, corrupting the recovered dates. UTC-anchoring round-trips.
        const toUtcDayStart = (d: Date) =>
          moment.utc(moment(d).format('YYYY-MM-DD'), 'YYYY-MM-DD').startOf('day').toISOString();
        const toUtcDayEnd = (d: Date) =>
          moment.utc(moment(d).format('YYYY-MM-DD'), 'YYYY-MM-DD').endOf('day').toISOString();
        const bookingSlots =
          mergedRanges.length > 0
            ? mergedRanges.map((s) => ({ startTime: toUtcDayStart(s.start), endTime: toUtcDayEnd(s.end) }))
            : undefined;
        const quantity = !isFullWindow && pickedDays.length > 0 ? pickedDays.length : Math.max(1, a.qty || 1);

        return {
          salesAssetId: a.salesAssetId || a.id,
          id: a.id,
          name: a.name,
          group: a.group,
          type: a.type,
          isAutomated: a.isAutomated ?? false,
          timeType: a.timeType,
          isActive: true,
          quantity,
          ...(bookingSlots ? { bookingSlots } : {}),
        };
      });

      return {
        // Include server DB id when editing/resuming so the backend updates rather than creates.
        ...(inst.serverPackageId ? { id: inst.serverPackageId } : {}),
        packageType: 'STANDARD_PACKAGE' as const,
        status: 'PENDING_BD' as const,
        startDate: toIsoDatetime(inst.startDate),
        endDate: toIsoDatetime(inst.endDate || inst.startDate),
        packageTier: inst.packageId || undefined,
        merchantComment: data.merchantComment || undefined,
        paymentDetails: { currency, salesPrice: String(inst.packagePrice ?? 0) },
        ...(assets.length > 0 ? { assets } : {}),
      };
    }),
  };
}

// ─── STANDALONE OFFER (DEAL) ────────────────────────────────

/**
 * Map the Standalone Offer form → CreateDealAssetRequest.
 * Title/description/URL/image go into contents[]; dates become startTime/endTime.
 */
export function mapOfferFormToRequest(data: OfferFormData, merchant: InitInfoMerchant): CreateDealAssetRequest {
  const title = (data.title || '').trim();
  const contents: ApiContentDto[] = [
    {
      platform: 'WEB',
      title,
      description: (data.description || '').trim() || undefined,
      landingPageUrl: (data.landingUrl || '').trim() || undefined,
      imageUrl: data.heroImageUrl || undefined,
      imageAlt: (data.bannerAlt || '').trim() || undefined,
    },
  ];

  return {
    opportunityId: data.linkedOpportunityId ? Number(data.linkedOpportunityId) : undefined,
    merchantId: merchant.merchantId || undefined,
    country: merchant.country,
    startTime: toIsoDatetime(data.startDate, data.startTime),
    endTime: toIsoDatetime(data.endDate, data.endTime),
    assetStatus: DealAssetStatus.PENDING_MKT,
    assetName: title || undefined,
    contents,
    affiliateLink: data.landingUrl?.trim() || undefined,
  };
}
